/**
 * Uzun süren kullanıcı işlemleri için "meşgul" durumu: toplu silme, SP hazırlığı, rapor…
 *
 * Bir işlem başlatılır, ilerledikçe güncellenir, bitince kapanır:
 *   const busy = startBusy({ scope: tabId, title: 'Kayıtlar siliniyor', total: 523 });
 *   busy.progress(120);  …  busy.end();
 *
 * Kapsam (`scope`) sekme kimliği: örtü yalnız o sekmeyi kaplar, kullanıcı başka sekmede
 * çalışmaya devam edebilir. Kapsamsız işlem (pencere içi, Node görünümü) tüm uygulamayı kaplar.
 *
 * Önceden bu işlemler ya hiç gösterge vermiyordu (toplu silme düğmesine basınca ekran bir süre
 * "hiçbir şey olmuyor" gibi duruyordu) ya da yalnız düğmeyi kilitliyordu.
 */
import { create } from 'zustand';

export interface BusyTask {
  id: number;
  scope: string | null;
  title: string;
  /** Beklerken sırayla gösterilen açıklamalar (ekranın canlı olduğu görülsün). */
  hints: string[];
  done: number | null;
  total: number | null;
  /** Verilirse "Durdur" düğmesi çıkar. */
  onCancel?: () => void;
  cancelling: boolean;
  startedAt: number;
}

interface BusyState {
  tasks: BusyTask[];
}

export const useBusyStore = create<BusyState>(() => ({ tasks: [] }));

let seq = 1;
const DIALOG_CHECK_MS = 120;

export interface BusyOptions {
  scope?: string | null;
  title: string;
  hints?: string[];
  total?: number | null;
  onCancel?: () => void;
}

export interface BusyHandle {
  /** İlerleme: tamamlanan (ve gerekirse yeni toplam). */
  progress: (done: number, total?: number | null) => void;
  update: (patch: Partial<Pick<BusyTask, 'title' | 'hints'>>) => void;
  end: () => void;
}

function patchTask(id: number, patch: Partial<BusyTask>) {
  useBusyStore.setState((s) => ({ tasks: s.tasks.map((t) => (t.id === id ? { ...t, ...patch } : t)) }));
}

/**
 * Ekranda açık bir pencere var mı (Radix Dialog / AlertDialog)? Varsa sekme kapsamlı gösterge
 * pencerenin ARKASINDA kalır: sekme kabı z-40, pencere z-50. Popup ızgaradan (sonuç penceresi,
 * lookup kütüphanesi) silerken "yükleniyor" popup'ın arkasında görünmüyordu.
 */
function isDialogOpen(): boolean {
  if (typeof document === 'undefined') return false;
  return !!document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]');
}

export function startBusy(opts: BusyOptions): BusyHandle {
  const id = seq++;
  const task: BusyTask = {
    id,
    scope: opts.scope ?? null,
    title: opts.title,
    hints: opts.hints ?? [],
    done: opts.total != null ? 0 : null,
    total: opts.total ?? null,
    cancelling: false,
    startedAt: Date.now(),
    onCancel: opts.onCancel
      ? () => {
          patchTask(id, { cancelling: true });
          opts.onCancel!();
        }
      : undefined,
  };
  useBusyStore.setState((s) => ({ tasks: [...s.tasks, task] }));
  let ended = false;
  /*
   * Pencere açıksa gösterge uygulama katmanına (pencerenin üstüne) taşınır. Kısa bir gecikmeyle
   * bakılır: silme onayı kapanırken pencere bir an daha "açık" görünüyor — hemen bakılsa ana
   * ızgaradaki silme de tüm uygulamayı örterdi. Gösterge zaten 300 ms'den önce çizilmiyor.
   */
  if (task.scope !== null) {
    setTimeout(() => {
      if (!ended && isDialogOpen()) patchTask(id, { scope: null });
    }, DIALOG_CHECK_MS);
  }
  return {
    progress: (done, total) => {
      if (!ended) patchTask(id, total === undefined ? { done } : { done, total });
    },
    update: (patch) => {
      if (!ended) patchTask(id, patch);
    },
    end: () => {
      if (ended) return;
      ended = true;
      useBusyStore.setState((s) => ({ tasks: s.tasks.filter((t) => t.id !== id) }));
    },
  };
}

/** `fn` sürerken meşgul gösterir; hata da olsa kapanır. */
export async function withBusy<T>(opts: BusyOptions, fn: (busy: BusyHandle) => Promise<T>): Promise<T> {
  const busy = startBusy(opts);
  try {
    return await fn(busy);
  } finally {
    busy.end();
  }
}
